/*****************************************************************************************
 * 설명 : 관리자 > 컨텐츠관리 > 사이트 관리
 * URI : /admin/site
*****************************************************************************************/
import Dialog from '@mui/material/Dialog';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import BtnAgGridSave from 'components/btnAgGridSave';
import ChipEx from 'components/chipEx';
import { BtnSearch } from 'components/common';
import { ButtonEx } from 'components/inputEx';
import alertMsg from 'components/message';

import { Restful } from 'service/restful';
import { comma, PaperComponent } from 'service/utils';

import { MESSAGE_DELAY } from 'config/config';

import useGridConfig from 'hooks/useGridConfig';

import Layout from 'pages/admin/layout/layout';
import SiteModal from './siteModal';

/*****************************************************************************************
 * 설명 : 함수 선언
*****************************************************************************************/
const Site = ( props ) => {

  /***************************************************************************************
   * 설명 : 변수 선언부
  ***************************************************************************************/
  const [gridApi, setGridApi] = useState({});
  const [getApi] = Restful();

  const currentMenu = useSelector(state => state?.menuList?.current);

  const [list, setList] = useState([]);

  const [openModal, setOpenModal] = useState({open: false, modal: 'add', data: []});

  // table column
  const [columnDefs, setColumnDefs] = useState([
    { headerName: '관리번호(관리자용)', field: 'seq', width: 30, cellClass: 'center', flex: 0,
      headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, checkboxSelection: true },
    { headerName: '번호', field: 'index', width: 55, cellClass: 'cp right',
      valueGetter: (params) => {
        return comma(params.node.rowIndex + 1);
      }
    },
    { headerName:'사이트명', field: 'siteName', width: 300, cellClass: 'cp' },
    { headerName:'도메인', field: 'domain', width: 300, cellClass: 'cp' },
    { headerName:'URI', field: 'uri', width: 300, cellClass: 'cp' },
    { headerName: '사용여부', field: 'isUse', width: 100, cellClass: 'cp center',
      cellRenderer: function(params) {
        if( params.data.isUse === '1' || params.data.isUse === 1 )
          return <ChipEx color="success" variant="outlined" size="small" label="사용" />;
        else
          return <ChipEx color="default" variant="outlined" size="small" label="사용안함" />;
      }
    },
    {headerName: '수정', field: 'modify', cellClass: 'cp center', width: 100,
      cellRenderer: function (params) {
        return <ButtonEx title="수정" auth="authModify" color="secondary" variant="outlined" className="gridBtn" onClick={() => setOpenModal({open: true, modal: 'add', data: params.data})} />;
      }
    }
  ]);

  const [gridConfig, setGridUpdate] = useGridConfig(1, setColumnDefs);

  /***************************************************************************************
   * 설명 : 사이트 리스트 가져오기
  ***************************************************************************************/
  const getSiteInfo = () => {
    let params = {
      program: 'api',
      service: 'cms',
      action: 'getSiteInfo',
      version: '1.0'
    }

    getApi("get", params).then( response => {
      if( response !== undefined && response.data.result ) {
        setList(response.data.siteInfo);
      } else {
        setList([]);
      }
    })
  }

  /***************************************************************************************
   * 설명 : 사이트 삭제
  ***************************************************************************************/
  const deleteSite = () => {
    let nodes = gridApi.getSelectedRows();

    if( nodes.length < 1 ) {
      alertMsg("삭제할 사이트를 선택하시기 바랍니다.", "W", MESSAGE_DELAY);
      return;
    }

    if( ! window.confirm("선택한 사이트를 삭제하시겠습니까?") ) return;

    let params = {
      program: 'api',
      service: 'cms',
      action: 'deleteSiteInfo',
      version: '1.0',
    }

    params.seq = nodes.map((item) => item.seq).join();

    getApi("post", params).then( response => {
      if( response !== undefined && response.data.result ) {
        alertMsg(response.data.message, "S", MESSAGE_DELAY);

        getSiteInfo();
      } else {
        alertMsg(response?.data?.message || '서버와의 통신에 실패하였습니다.', 'E', MESSAGE_DELAY);
      }
    })
  }

  /***************************************************************************************
   * 설명 : 데이터 처리
  ***************************************************************************************/
  useEffect(() => {
    getSiteInfo();

    // eslint-disable-next-line
  }, [window.location.search]);

  /***************************************************************************************
   * 설명 : html 선언부
  ***************************************************************************************/
  return (
    <Layout>
      <div className="admin-cms-contents">
        <header className="admin-cms-search-header">
          <div className="left">
            <BtnSearch click={() => getSiteInfo()}></BtnSearch>
            <BtnAgGridSave click={setGridUpdate}></BtnAgGridSave>
          </div>

          <div className="right">
            <ButtonEx
              color="error"
              variant="outlined"
              auth="authDelete"
              title="사이트삭제"
              onClick={() => deleteSite()}
            />

            <ButtonEx
              color="primary"
              className="ml5"
              variant="contained"
              auth="authWrite"
              title="사이트등록"
              onClick={() => {
                setOpenModal({open: true, modal: 'add', data: {}})
              }}
            />
          </div>
        </header>

        <section className="admin-cms-body">
          <section className="ag-theme-balham grid" style={{height:' calc(100% - 50px)'}}>
            <AgGridReact
              defaultColDef={{
                sortable: true,
                resizable: true,
                filter: false,
                lockVisible: true
              }}
              rowSelection = {'multiple'}
              columnDefs={columnDefs}
              rowData={list}
              onGridReady={(event) => {
                setGridApi(event.api);
                gridConfig(event.columnApi);
              }}
              onRowDoubleClicked={(event) => {
                if( currentMenu?.authModify === '1' || currentMenu?.authModify === 1 )
                  setOpenModal({open: true, modal: 'add', data: event.data});
              }}
              overlayNoRowsTemplate = "검색된 내용이 없습니다."
            />
          </section>
        </section>
      </div>

      { openModal.open &&
        <Dialog
          open={openModal.open}
          onClose={() => (event, reason) => {
            if(reason && reason === "backdropClick") return;
            setOpenModal({open: false, modal: openModal.modal, data: openModal.data});
          }}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
          sx={{
            "& .MuiDialog-container": {
              "& .MuiPaper-root": {
                width: "100%",
                maxWidth: "600px",  // Set your width here
              },
            },
          }}
        >
          <SiteModal
            open={openModal.open}
            close={() => setOpenModal({ open: false, modalName: 'add', data: openModal.data })}
            data={openModal.data}
            getList={getSiteInfo}
            indicator={props.indicator}
            history={props.history}
          />
        </Dialog>
      }
    </Layout>
  );
}

/*****************************************************************************************
 * 설명 : default export 선언
*****************************************************************************************/
export default Site;